import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  SopAssessmentStatusSchema,
  SopEvidenceReferenceSchema,
  SopFindingSchema,
  SopImprovementImpactWarningSchema,
  SopImprovementStatusSchema,
  SopLearningSettingsSchema,
  SopUsageEvidenceSchema,
} from "@erp/shared-types";
import { EditableSopStepSchema, SopParamSchema } from "./agent.contract";

export const SopImprovementEditSchema = z.object({
  sopId: z.uuid(),
  baseVersionId: z.uuid(),
  steps: z.array(EditableSopStepSchema),
  parameters: z.array(SopParamSchema),
});
export type SopImprovementEdit = z.infer<typeof SopImprovementEditSchema>;
export const SopImprovementProposalSchema = z.object({
  id: z.uuid(),
  sopId: z.uuid(),
  sopTitle: z.string(),
  taskId: z.uuid(),
  baseVersionId: z.uuid(),
  baseVersion: z.number().int(),
  status: SopImprovementStatusSchema,
  reviewRevision: z.number().int(),
  summary: z.string(),
  rationale: z.string(),
  contentHash: z.string(),
  edit: SopImprovementEditSchema,
  beforeSteps: z.array(EditableSopStepSchema),
  beforeParameters: z.array(SopParamSchema),
  impactWarnings: z.array(SopImprovementImpactWarningSchema),
  usages: z.array(SopUsageEvidenceSchema),
  evidence: z.array(SopEvidenceReferenceSchema),
  acceptedByUserId: z.string().nullable(),
  draftVersionId: z.uuid().nullable(),
  draftVersion: z.number().int().nullable(),
  draftStatus: z.enum(["draft", "finalized", "deleted"]).nullable(),
  published: z.boolean(),
  dismissalReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SopImprovementProposal = z.infer<
  typeof SopImprovementProposalSchema
>;
export const SopAssessmentViewSchema = z.object({
  taskId: z.uuid(),
  enabled: z.boolean(),
  status: SopAssessmentStatusSchema,
  requestedRevision: z.number().int(),
  processedRevision: z.number().int(),
  assessmentId: z.uuid().nullable(),
  error: z.string().nullable(),
  findings: z.array(SopFindingSchema),
  proposalIds: z.array(z.uuid()),
  updatedAt: z.string().nullable(),
});
export type SopAssessmentView = z.infer<typeof SopAssessmentViewSchema>;

const idInput = z.object({ proposalId: z.uuid() });
const decisionInput = idInput.extend({
  expectedRevision: z.number().int().positive(),
});
export const sopImprovementsContract = {
  getSettings: oc
    .input(z.object({ taskId: z.uuid() }))
    .output(SopLearningSettingsSchema)
    .route({
      summary: "Get procedure learning setting for a task",
      description:
        "Returns whether feedback on a task is used to propose improvements to the agent's procedures (SOPs), and whether the current user can change it.",
    }),
  updateSettings: oc
    .input(z.object({ taskId: z.uuid(), enabled: z.boolean() }))
    .output(SopLearningSettingsSchema)
    .route({
      summary: "Toggle procedure learning for a task",
      description:
        "Enables or disables using a task's feedback to propose procedure (SOP) improvements, and queues a fresh assessment of existing feedback.",
    }),
  list: oc
    .input(
      z.object({
        sopId: z.uuid().optional(),
        taskId: z.uuid().optional(),
        status: SopImprovementStatusSchema.optional(),
        limit: z.number().int().min(1).max(100).default(25),
        offset: z.number().int().min(0).default(0),
      }),
    )
    .output(
      z.object({
        proposals: z.array(SopImprovementProposalSchema),
        total: z.number().int(),
      }),
    )
    .route({
      summary: "List procedure improvement proposals",
      description:
        "Pages through AI-proposed edits to agent procedures (SOPs), filterable by procedure, task and status (pending, accepted, dismissed, stale, superseded).",
    }),
  get: oc.input(idInput).output(SopImprovementProposalSchema).route({
    summary: "Get a procedure improvement proposal",
    description:
      "Returns one proposed procedure (SOP) edit with summary, rationale, before and after steps and parameters, impact warnings, usages, evidence and resulting draft.",
  }),
  accept: oc
    .input(decisionInput)
    .output(
      z.object({
        draftVersionId: z.uuid(),
        draftVersion: z.number().int(),
        sopId: z.uuid(),
      }),
    )
    .route({
      summary: "Accept a procedure improvement",
      description:
        "Accepts a proposed procedure (SOP) edit by creating a new draft version of the procedure with the change; it is not published. Fails if the task feedback changed.",
    }),
  dismiss: oc
    .input(decisionInput.extend({ reason: z.string().trim().min(1).max(4000) }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Dismiss a procedure improvement",
      description:
        "Dismisses a proposed procedure (SOP) edit with a reason so it is not applied.",
    }),
  regenerate: oc
    .input(decisionInput)
    .output(z.object({ taskId: z.uuid() }))
    .route({
      summary: "Regenerate a procedure improvement",
      description:
        "Marks a proposed procedure (SOP) edit superseded and queues a new assessment of the task's feedback to produce a fresh proposal.",
    }),
  getAssessment: oc
    .input(z.object({ taskId: z.uuid() }))
    .output(SopAssessmentViewSchema)
    .route({
      summary: "Get a task's procedure assessment",
      description:
        "Returns the status of the AI review of a task's feedback for procedure (SOP) improvements, its findings, any error and the proposals it produced.",
    }),
  retryAssessment: oc
    .input(z.object({ taskId: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Retry a task's procedure assessment",
      description:
        "Queues the AI review of a task's feedback for procedure (SOP) improvements to run again.",
    }),
  getEvidence: oc
    .input(z.object({ receiptId: z.uuid() }))
    .output(
      z.object({
        reference: SopEvidenceReferenceSchema,
        body: z.string().nullable(),
        superseded: z.boolean(),
      }),
    )
    .route({
      summary: "Get procedure feedback evidence",
      description:
        "Returns one piece of task feedback cited by a procedure improvement, with its text and whether newer feedback replaced it; private chat feedback is author-only.",
    }),
};
